import { AppliedVoucherState, CartItem, CartState } from 'src/slices/cart';
import { CampaignJoinInfoDto, OrderJoinDetailsDto, ProductJoinInfoDto } from 'src/types/generated';
import { getCampaignProductPrice } from './campaign';
import { SelectedCampaignProduct } from 'src/slices/campaign';

// Google events https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item_list

// Facebook https://www.facebook.com/business/m/one-sheeters/facebook-pixel-events
// Facebook event params https://developers.facebook.com/docs/meta-pixel/implementation/conversion-tracking/

const getCampaignPrice = (campaignProducts: SelectedCampaignProduct[], products: ProductJoinInfoDto[]) => {
  return (campaignProducts || [])
    .filter((sp) => sp.isFreeSlot === false)
    .map(({ productId, variantId, partials }) => getCampaignProductPrice({ productId, variantId, products, partials }))
    .reduce((totalPrice, price) => totalPrice + price, 0);
};

const normalizeJSFloat = (num: number) =>
  parseFloat((typeof num === 'number' && !Number.isNaN(num) ? num : 0)?.toFixed(2));

const currency = 'EUR' as const;

const getProductType = (product: ProductJoinInfoDto) => {
  return product.categories?.[0]?.name || 'Unknown';
};

const getCartItemPrice = (cartItem: CartItem, products: ProductJoinInfoDto[], campaigns: CampaignJoinInfoDto[]) => {
  // Handle voucher free products - they don't have a productId in products list
  if (cartItem.isVoucherFreeProduct) {
    return { price: 0, product: null, campaign: null, freeProductName: cartItem.freeProductName };
  }

  const product = cartItem.isProduct && cartItem.productId && products.find((p) => p.id === cartItem.productId);
  const campaign = cartItem.isCampaign && cartItem.campaignId && campaigns.find((c) => c.id === cartItem.campaignId);

  let price = 0;
  if (product) {
    price = getCampaignProductPrice({
      products,
      partials: cartItem.partials,
      productId: product.id,
      product,
      variantId: cartItem.variantIds[0],
    });
  } else if (campaign) {
    price = getCampaignPrice(cartItem.products, products);
  }
  return { price: normalizeJSFloat(price), product, campaign, freeProductName: null };
};

export const trackProductView = (product: ProductJoinInfoDto) => {
  window.gtag('event', 'view_item', {
    currency,
    value: product.price,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        price: product.price,
      },
    ],
  });

  window.fbq('track', 'ViewContent', {
    currency,
    content_name: product.name,
    content_ids: [product.id],
    content_type: 'product',
    product_type: getProductType(product),
    contents: [{ id: product.id, title: product.name, price: product.price }],
    value: product.price,
  });
};

export const trackCampaignView = (campaign: CampaignJoinInfoDto) => {
  window.gtag('event', 'view_item', {
    currency,
    value: 0,
    items: [
      {
        item_id: campaign.id,
        item_name: campaign.name,
      },
    ],
  });

  window.fbq('track', 'ViewContent', {
    currency,
    content_name: campaign.name,
    content_ids: [campaign.id],
    content_type: 'product',
    product_type: 'Campaign',
    contents: [{ id: campaign.id, title: campaign.name, price: 0 }],
    value: 0,
  });
};

export const trackRecommendationImpression = (campaign: CampaignJoinInfoDto) => {
  window.gtag('event', 'view_promotion', {
    creative_name: 'cart_campaign_recommendation',
    promotion_id: campaign.id,
    promotion_name: campaign.name,
  });

  window.fbq('trackCustom', 'ViewRecommendation', {
    content_name: campaign.name,
    content_ids: [campaign.id],
    content_type: 'campaign_recommendation',
  });
};

export const trackRecommendationClick = (campaign: CampaignJoinInfoDto, product: ProductJoinInfoDto) => {
  window.gtag('event', 'select_promotion', {
    creative_name: 'cart_campaign_recommendation',
    promotion_id: campaign.id,
    promotion_name: campaign.name,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        price: product.price,
      },
    ],
  });

  window.fbq('trackCustom', 'SelectRecommendation', {
    content_name: product.name,
    content_ids: [product.id],
    content_type: 'campaign_recommendation',
    promotion_id: campaign.id,
    promotion_name: campaign.name,
    value: product.price,
    currency,
  });
};

export const trackItemAddedToCart = (
  cartItem: CartItem,
  products: ProductJoinInfoDto[],
  campaigns: CampaignJoinInfoDto[]
) => {
  const { price, product, campaign, freeProductName } = getCartItemPrice(cartItem, products, campaigns);
  const itemName = product?.name ?? campaign?.name ?? freeProductName ?? 'Free product';
  const id = product?.id ?? campaign?.id ?? cartItem.uniqId;
  window.gtag('event', 'add_to_cart', {
    currency,
    value: normalizeJSFloat(price * (cartItem.quantity ?? 1)),
    items: [
      {
        item_id: id,
        item_name: itemName,
        price,
        item_variant: product?.ektacoCode,
        quantity: cartItem.quantity,
      },
    ],
  });

  window.fbq('track', 'AddToCart', {
    currency,
    content_name: itemName,
    content_ids: [id],
    content_type: 'product',
    product_type: product ? getProductType(product) : 'Campaign',
    contents: [{ id, quantity: cartItem.quantity, price, title: itemName, variant: product?.ektacoCode }],
    value: normalizeJSFloat(price * (cartItem.quantity ?? 1)),
  });
};

export const trackItemRemovedFromCart = (
  cartItem: CartItem,
  products: ProductJoinInfoDto[],
  campaigns: CampaignJoinInfoDto[]
) => {
  const { price, product, campaign, freeProductName } = getCartItemPrice(cartItem, products, campaigns);
  const itemName = product?.name ?? campaign?.name ?? freeProductName ?? 'Free product';
  const id = product?.id ?? campaign?.id ?? cartItem.uniqId;
  window.gtag('event', 'remove_from_cart', {
    currency,
    value: normalizeJSFloat(price * (cartItem.quantity ?? 1)),
    items: [
      {
        item_id: id,
        item_name: itemName,
        price,
        item_variant: product?.ektacoCode,
        quantity: cartItem.quantity,
      },
    ],
  });

  window.fbq('track', 'RemoveFromCart', {
    currency,
    content_name: itemName,
    content_ids: [id],
    content_type: 'product',
    product_type: product ? getProductType(product) : 'Campaign',
    contents: [{ id, quantity: cartItem.quantity, price, title: itemName, variant: product?.ektacoCode }],
    value: normalizeJSFloat(price * (cartItem.quantity ?? 1)),
  });
};

export const trackCartItemQuantityChange = (
  cartItem: CartItem,
  oldQuantity: number,
  newQuantity: number,
  products: ProductJoinInfoDto[],
  campaigns: CampaignJoinInfoDto[]
) => {
  const quantityDiff = newQuantity - oldQuantity;

  if (quantityDiff === 0) return;

  const { price, product, campaign, freeProductName } = getCartItemPrice(cartItem, products, campaigns);
  const itemName = product?.name ?? campaign?.name ?? freeProductName ?? 'Free product';
  const id = product?.id ?? campaign?.id ?? cartItem.uniqId;
  const absQuantityDiff = Math.abs(quantityDiff);

  if (quantityDiff > 0) {
    // Quantity increased - fire AddToCart
    window.gtag('event', 'add_to_cart', {
      currency,
      value: normalizeJSFloat(price * absQuantityDiff),
      items: [
        {
          item_id: id,
          item_name: itemName,
          price,
          item_variant: product?.ektacoCode,
          quantity: absQuantityDiff,
        },
      ],
    });

    window.fbq('track', 'AddToCart', {
      currency,
      content_name: itemName,
      content_ids: [id],
      content_type: 'product',
      product_type: product ? getProductType(product) : 'Campaign',
      contents: [{ id, quantity: absQuantityDiff, price, title: itemName, variant: product?.ektacoCode }],
      value: normalizeJSFloat(price * absQuantityDiff),
    });
  } else {
    // Quantity decreased - fire RemoveFromCart
    window.gtag('event', 'remove_from_cart', {
      currency,
      value: normalizeJSFloat(price * absQuantityDiff),
      items: [
        {
          item_id: id,
          item_name: itemName,
          price,
          item_variant: product?.ektacoCode,
          quantity: absQuantityDiff,
        },
      ],
    });

    window.fbq('track', 'RemoveFromCart', {
      currency,
      content_name: itemName,
      content_ids: [id],
      content_type: 'product',
      product_type: product ? getProductType(product) : 'Campaign',
      contents: [{ id, quantity: absQuantityDiff, price, title: itemName, variant: product?.ektacoCode }],
      value: normalizeJSFloat(price * absQuantityDiff),
    });
  }
};

export const trackVoucherApplied = (voucher: AppliedVoucherState) => {
  window.gtag('event', 'apply_coupon', {
    coupon: voucher.code,
    discount: normalizeJSFloat(voucher.discountAmount),
    coupon_type: voucher.type,
  });

  window.fbq('trackCustom', 'ApplyCoupon', {
    currency,
    coupon: voucher.code,
    discount: normalizeJSFloat(voucher.discountAmount),
    coupon_type: voucher.type,
  });
};

export const trackVoucherRemoved = (code: string) => {
  window.gtag('event', 'remove_coupon', { coupon: code });
  window.fbq('trackCustom', 'RemoveCoupon', { coupon: code });
};

export const trackBeginCheckout = (
  cart: CartState,
  products: ProductJoinInfoDto[],
  campaigns: CampaignJoinInfoDto[],
  total: number,
  appliedVoucher?: AppliedVoucherState | null
) => {
  window.gtag('event', 'begin_checkout', {
    currency,
    value: normalizeJSFloat(total),
    ...(appliedVoucher && { coupon: appliedVoucher.code }),
    items: cart.cartItems.map((cartItem) => {
      const { price, product, campaign, freeProductName } = getCartItemPrice(cartItem, products, campaigns);
      return {
        item_id: product?.id ?? campaign?.id ?? cartItem?.uniqId,
        item_name: product?.name ?? campaign?.name ?? freeProductName ?? 'Free product',
        quantity: cartItem.quantity,
        item_variant: product?.ektacoCode,
        price,
      };
    }),
  });

  const contents = cart.cartItems.map((cartItem) => {
    const { price, product, campaign, freeProductName } = getCartItemPrice(cartItem, products, campaigns);
    const id = product?.id ?? campaign?.id ?? cartItem?.uniqId;
    const productName = product?.name ?? campaign?.name ?? freeProductName ?? 'Free product';
    return {
      id,
      title: productName,
      price,
      quantity: cartItem.quantity,
      variant: product?.ektacoCode,
    };
  });

  window.fbq('track', 'InitiateCheckout', {
    currency,
    content_ids: contents.map((c) => c.id),
    content_type: 'product',
    contents,
    value: normalizeJSFloat(total),
    num_items: cart.cartItems.length,
    ...(appliedVoucher && { coupon: appliedVoucher.code }),
  });
};

export const trackPurchase = (order: OrderJoinDetailsDto, products: ProductJoinInfoDto[]) => {
  if (order.status === 'unpaid') return;

  const getProductPrice = (orderItem: OrderJoinDetailsDto['items'][number]) => {
    const product = (products || []).find((p) => p.id === orderItem.productId);
    const price =
      (orderItem.isFreeCampaignProduct
        ? 0
        : product &&
          getCampaignProductPrice({
            products,
            partials: orderItem.partials,
            productId: product.id,
            product,
            variantId: orderItem.variants?.[0]?.variantId,
          })) ?? orderItem.price;
    return normalizeJSFloat(price);
  };

  window.gtag('event', 'purchase', {
    currency,
    transaction_id: order.id,
    value: order.totalPrice,
    ...(order.voucherCode && { coupon: order.voucherCode }),
    ...(order.voucherDiscountAmount && { discount: normalizeJSFloat(order.voucherDiscountAmount) }),
    items: order.items.map((orderItem) => ({
      item_id: orderItem.productId,
      item_name: orderItem.name,
      quantity: orderItem.quantity,
      item_variant: orderItem.ektacoCode,
      price: getProductPrice(orderItem),
    })),
  });

  window.fbq('track', 'Purchase', {
    currency,
    value: order.totalPrice,
    content_type: 'product',
    ...(order.voucherCode && { coupon: order.voucherCode }),
    ...(order.voucherDiscountAmount && { discount: normalizeJSFloat(order.voucherDiscountAmount) }),
    ...(order.voucherType && { coupon_type: order.voucherType }),
    contents: order.items.map((orderItem) => ({
      id: orderItem.productId,
      title: orderItem.name,
      quantity: orderItem.quantity,
      price: getProductPrice(orderItem),
      variant: orderItem.ektacoCode,
    })),
    contents_ids: order.items.map((c) => c.productId),
    num_items: order.items.length,
  });
};
